import {useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../utils/site-config'
import {resolveFeatureFlag} from '../utils/feature-flag-state'
import {useFeatureFlagDecisions} from './use-feature-flag-decisions'

const sentImpressions = new Set()

const warn = (message) => {
    if (process.env.NODE_ENV === 'production') return
    console.warn(`[feature-flag] ${message}`)
}

/**
 * Read one feature flag. Pass the frozen definition, not its key string.
 * `options` are the component defaults an Optimizely feature_payload may replace.
 */
export const useFeatureFlag = (definition, settings = {}) => {
    const {site} = useMultiSite()
    const location = useLocation()
    const {app: appConfig} = getConfig() || {}
    const siteId = site?.id

    if (typeof definition === 'string') {
        warn(`useFeatureFlag received "${definition}". Pass the definition object.`)
    }
    if (Object.prototype.hasOwnProperty.call(settings, 'fallbackValue')) {
        warn('fallbackValue belongs on the definition, not the hook settings. It was ignored.')
    }

    const mode =
        settings.mode === 'experiment' || settings.mode === 'preference'
            ? settings.mode
            : definition?.sfcc
            ? 'preference'
            : 'experiment'

    if (settings.isExposed !== undefined && mode !== 'experiment') {
        warn('isExposed only applies in experiment mode and was ignored.')
    }
    if (!definition?.sfcc && mode === 'preference') {
        warn(
            `${definition?.id || 'This flag'} has no SFCC preference, so mode must be "experiment".`
        )
    }

    const decisionState = useFeatureFlagDecisions(
        mode === 'experiment' && typeof definition === 'object'
    )
    const sfccValue = definition?.sfcc
        ? getSiteEnvVarValue(appConfig, definition.id, siteId)
        : undefined
    const decision = decisionState.decisions?.[definition?.flagKey]

    const resolved = resolveFeatureFlag({
        definition: typeof definition === 'object' ? definition : null,
        settings: {mode, isExposed: settings.isExposed, options: settings.options},
        sfccValue,
        decision,
        decisionsStatus: decisionState.status
    })

    useEffect(() => {
        if (!resolved.shouldImpress || !definition?.flagKey) return
        const impressionKey = `${location.key || location.pathname}:${definition.flagKey}`
        if (sentImpressions.has(impressionKey)) return
        sentImpressions.add(impressionKey)

        const headers = {
            'Content-Type': 'application/json',
            ...decisionState.headers
        }
        const query = decisionState.queryString
        const url = query
            ? `${decisionState.fetchOrigin}/feature-flag-impression?${query}`
            : `${decisionState.fetchOrigin}/feature-flag-impression`
        fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({flagKey: definition.flagKey})
        }).catch(() => {})
    }, [
        resolved.shouldImpress,
        definition?.flagKey,
        location.key,
        location.pathname,
        decisionState.fetchOrigin,
        decisionState.queryString,
        decisionState.headers
    ])

    return {
        value: resolved.value,
        values: resolved.values,
        isActive: resolved.isActive,
        matches: resolved.matches,
        options: resolved.options,
        status: resolved.status,
        isReady: resolved.isReady,
        source: resolved.source
    }
}
